import { produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import type { Address } from '../../../../../Shared/js/@types/Address';
import { RootState } from '../../../../../Shared/js/redux/rootReducer';
import AddressChangeTracker from '../../../../../Shared/React/js/components/addressSearch/AddressChangeTracker';
import { dispatchAddressChangedEvent } from '../../../../../Shared/React/js/components/addressSearch/AddressSearch';
import { AppComponent } from '../../../../../Shared/React/js/components/AppComponent';
import { usePrevious } from '../../../../../Shared/React/js/utils/Hooks';
import type { SearchCriteria } from '../../../../js/@types/SearchCriteria';
import type { SearchModel } from '../../../../js/@types/SearchModel';
import { broadbandApi } from '../../../../js/redux/broadbandApi';
import { actions as broadbandActions, mapSearchModelToState } from '../../../../js/redux/broadbandSlice';
import { actions as modalActions } from '../../../../js/redux/modalSlice';
import { actions, SearchSliceState } from '../../../../js/redux/searchSlice';
import LegacyActions from '../../actions/BroadbandActions';
import { generateQueryString } from '../../utils/BroadbandQueryString';
import Filters from '../filters/Filters';
import { AvailabilityCheckModal } from '../product/availabilityCheck/AvailabilityCheckPanel';

const connector = connect(
    (
        state: RootState,
        ownProps: {
            initialState: SearchModel;
            criteria?: SearchCriteria;
            searchSettings?: SearchSliceState['settings'];
            isInitialised?: boolean;
        }
    ) => {
        const isInitialised = !isEmpty(state.broadband?.search);
        const searchState = isInitialised ? state.broadband?.search : mapSearchModelToState(ownProps.initialState);

        return produce(ownProps, draft => {
            draft.criteria = searchState.criteria;
            draft.searchSettings = searchState.settings;
            draft.isInitialised = isInitialised;
        });
    },

    {
        init: broadbandActions.init,
        searchSuccess: actions.searchSuccess,
        searchError: actions.searchError,
        showModal: modalActions.showModal,
        ...actions
    }
);

export type Props = ConnectedProps<typeof connector>;

const QueryHelper = {
    getQueryString: (criteria: SearchCriteria, settings: SearchSliceState['settings']) => {
        return isEmpty(criteria)
            ? undefined
            : generateQueryString(criteria, settings.allConnectionsSelected, settings.embeddedMode);
    },
    isEquivalent: (target: SearchCriteria, compareTo: SearchCriteria[], settings: SearchSliceState['settings']) => {
        const resetIgnoredProperties = (criteria: SearchCriteria) => {
            // Properties that are not used by the search
            // should be ignored for triggering the search request
            return produce(criteria, draft => {
                // Whould be good to fetch the results per tab for better performance and smaller page size,
                // instead of fetching all tabs at once as we do now
                draft.common.tab = null;
            });
        };

        const getClearQuery = (c: SearchCriteria) => QueryHelper.getQueryString(resetIgnoredProperties(c), settings);

        const query = getClearQuery(target);

        const targetConnectionTypes = target.connectionTypes?.values?.slice().sort() ?? [];
        const hasConnectionTypesDiff = compareTo.every(c => {
            const compareConnectionTypes = c.connectionTypes?.values?.slice().sort() ?? [];
            return !isEqual(targetConnectionTypes, compareConnectionTypes);
        });

        if (hasConnectionTypesDiff) {
            return false;
        }

        return compareTo.some(c => isEqual(query, getClearQuery(c)));
    }
};

const SearchComponent = (props: Props) => {
    const isInitialised = useRef(false);
    const [isProgress, setIsProgress] = useState(false);

    const [criteria, setCriteria] = useState(props.criteria);
    const previousCriteria = usePrevious(criteria);
    const defaultCriteria = useRef(cloneDeep(criteria));

    const [triggerGetSearchResultsQuery, searchResultsQuery] = broadbandApi.useLazyGetSearchResultsQuery();

    // TODO: Rewrite this
    const legacyActions = useRef(new LegacyActions());

    useEffect(() => {
        const selector = '#search-results-partial';
        const startProgress = () => WhistleOut.startProgress(selector, 0.1, 50);
        const endProgress = () => WhistleOut.endProgress(selector);

        if (isProgress) {
            startProgress();
        } else {
            endProgress();
        }
        return () => endProgress();
    }, [isProgress]);

    const searchPageRefresh = useCallback(
        (criteria: SearchCriteria) => {
            legacyActions.current.searchPageRefresh(
                criteria,
                props.searchSettings.searchUrl,
                props.searchSettings.suppressCoverageCheck,
                props.searchSettings.coverageUrl
            );
        },
        [props.searchSettings]
    );

    const search = useCallback(
        (criteria: SearchCriteria, force?: boolean) => {
            const queryString = QueryHelper.getQueryString(criteria, props.searchSettings);
            const hasConnectionParam = queryString && queryString.indexOf('connection=') !== -1;
            const shouldSignalAllConnections =
                props.searchSettings.allConnectionsSelected === true && !hasConnectionParam;
            const requestQueryString = shouldSignalAllConnections
                ? WhistleOut.appendQueryParam(queryString || '', 'allconnections=true')
                : queryString;

            triggerGetSearchResultsQuery(
                {
                    baseUrl: props.initialState.resultsUrl,
                    queryString: requestQueryString
                },
                !force
            );
        },
        [props.initialState.resultsUrl, props.searchSettings, triggerGetSearchResultsQuery]
    );

    const applyUrlProviderOverrides = useCallback(
        (baseCriteria: SearchCriteria) => {
            if (typeof window === 'undefined') return baseCriteria;

            const params = new URLSearchParams(window.location.search);
            const rawSupplier = params.get('supplier');
            const rawCustomer = params.get('customer');

            const suppliers = rawSupplier
                ? rawSupplier
                      .split(',')
                      .map(s => s.trim())
                      .filter(Boolean)
                : null;

            if (!suppliers || suppliers.length === 0) {
                return baseCriteria;
            }

            const normalizeSupplierKey = (value: string) =>
                (value || '')
                    .trim()
                    .toLowerCase()
                    .replace(/&/g, 'and')
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '');

            const allSuppliers = props.searchSettings?.suppliers?.allItems || props.searchSettings?.suppliers?.items;
            const resolvedSupplierShortUrls = (suppliers || [])
                .map(v => {
                    const candidate = v.trim();
                    const candidateLower = candidate.toLowerCase();

                    if (!allSuppliers || allSuppliers.length === 0) {
                        return normalizeSupplierKey(candidate);
                    }

                    // 1) Direct shortUrl match (case-insensitive)
                    const direct = allSuppliers.find(s => s.shortUrl?.toLowerCase() === candidateLower);
                    if (direct?.shortUrl) return direct.shortUrl;

                    // 2) Match by slugified supplier name
                    const candidateNorm = normalizeSupplierKey(candidate);
                    const byName = allSuppliers.find(s => normalizeSupplierKey(s.name) === candidateNorm);
                    if (byName?.shortUrl) return byName.shortUrl;

                    // 3) Fallback: normalized candidate
                    return candidateNorm;
                })
                .filter(Boolean);

            return produce(baseCriteria, draft => {
                if (!draft.common) {
                    // Shouldn't happen, but keeps us safe if criteria is partial
                    draft.common = {} as SearchCriteria['common'];
                }

                draft.common.suppliers = { values: resolvedSupplierShortUrls };

                if (rawCustomer && rawCustomer.trim().length > 0) {
                    draft.common.customerType = rawCustomer.trim();
                }
            });
        },
        [props.searchSettings?.suppliers]
    );

    const handleShowModal = useCallback(
        (tabName?: string) => {
            const criteriaForModal = applyUrlProviderOverrides(props.criteria);
            props.showModal({
                tabName,
                criteria: criteriaForModal
            });
        },
        [applyUrlProviderOverrides, props]
    );

    const retrySearch = useCallback(() => {
        if (props.initialState.embeddedMode) {
            searchPageRefresh(criteria);
            return;
        }
        search(criteria, true);
    }, [props.initialState.embeddedMode, search, searchPageRefresh, criteria]);

    legacyActions.current.props = {
        embeddedMode: props.initialState.embeddedMode,
        nbnConnectionTypes: props.initialState.filters?.nbnConnectionTypes,
        criteria: criteria,
        defaultCriteria: defaultCriteria.current,
        coverage: props.initialState.state.coverage,
        coverageAddress: props.initialState.state.coverageAddress,
        searchSettings: props.searchSettings
    };
    legacyActions.current.actions = {
        searchSuccess: props.searchSuccess,
        searchError: props.searchError,
        searchRetry: retrySearch,

        disableMaximumResultLimits: props.disableMaximumResultLimits,
        hideFeaturedResults: props.hideFeaturedResults,
        includeNbnConnections: props.includeNbnConnections,
        updateConnectionTypes: props.updateConnectionTypes,
        updateContractType: props.updateContractType,
        updateCriteria: props.updateCriteria,
        updateData: props.updateData,
        updateModem: props.updateModem,
        updatePlanSummarySupplier: props.updatePlanSummarySupplier,
        updateSort: props.updateSort,
        updateSpeed: props.updateSpeed,
        updateSuppliers: props.updateSuppliers,
        updateTab: props.updateTab,
        showModal: handleShowModal,
        widenResults: props.widenResults
    };

    const handleAddressChanged = useCallback(
        (address: Address) => {
            if (address?.label === criteria.common.address?.label) {
                return;
            }

            dispatchAddressChangedEvent({
                // TODO: Fix eslint error
                // eslint-disable-next-line @typescript-eslint/no-invalid-this
                sender: this,
                address: address
            });

            props.updateAddress(address);
            WhistleOut.setCoverageCookie(null);
        },
        [criteria.common.address?.label, props]
    );

    const handleSpeedTestResultsChanged = useCallback((e: { detail: unknown }) => {
        if (!e.detail) {
            return;
        }
        legacyActions.current.bindResults();
    }, []);

    const handleTransactUrlChange = useCallback(() => {
        setIsProgress(false);
    }, []);

    const handleShowPlans = useCallback(
        (address: Address) => {
            setIsProgress(false);
            WhistleOut.scrollTo('body');
            handleAddressChanged(address);
        },
        [handleAddressChanged]
    );

    useEffect(() => {
        const init = () => {
            if (!isInitialised.current) {
                props.init(props.initialState);
                legacyActions.current.init();
                isInitialised.current = true;
            }
        };

        init();
        wo$(document).on('woSpeedTest.resultUpdated', handleSpeedTestResultsChanged);

        return () => {
            wo$(document).off('woSpeedTest.resultUpdated', handleSpeedTestResultsChanged);
        };
    }, [handleSpeedTestResultsChanged, props]);

    useEffect(() => setCriteria(props.criteria), [props.criteria]);
    useEffect(() => setIsProgress(searchResultsQuery.isFetching), [searchResultsQuery.isFetching]);

    useEffect(() => {
        const onCriteriaChange = () => {
            if (!props.isInitialised) {
                return;
            }

            if (
                QueryHelper.isEquivalent(
                    criteria,
                    [previousCriteria, props.searchSettings.resultsSearchCriteria],
                    props.searchSettings
                )
            ) {
                return;
            }

            if (props.initialState.embeddedMode && !props.searchSettings.enforceAjax) {
                searchPageRefresh(criteria);
                return;
            }

            search(criteria);
        };
        onCriteriaChange();
    }, [
        criteria,
        previousCriteria,
        props.initialState.embeddedMode,
        props.isInitialised,
        props.searchSettings,
        search,
        searchPageRefresh
    ]);

    useEffect(() => {
        const onSearchResult = () => {
            if (searchResultsQuery.isFetching) {
                return;
            }

            if (searchResultsQuery.error) {
                legacyActions.current.onAjaxSearchError();
                return;
            }

            if (!searchResultsQuery.data) {
                return;
            }

            legacyActions.current.onAjaxSearchSuccess(searchResultsQuery.data);
        };
        onSearchResult();
    }, [searchResultsQuery.data, searchResultsQuery.error, searchResultsQuery.isFetching]);

    const addressSearch = useMemo(() => props.initialState.addressSearch, [props.initialState.addressSearch]);

    return (
        <>
            <AddressChangeTracker onAddressChange={handleAddressChanged} initialValue={criteria.common.address} />
            <AvailabilityCheckModal
                shouldOpenTransactLink
                address={criteria.common.address}
                countryCode={addressSearch.countryCode}
                addressSearchPlaceHolder={addressSearch.text.addressSearchPlaceHolder}
                tooltipLink={addressSearch.text.tooltipLink}
                tooltipDescription={addressSearch.text.tooltipDescription}
                queryDelay={addressSearch.queryDelay}
                apiKey={addressSearch.apiKey}
                isSearchPage={!props.searchSettings.embeddedMode}
                onTransactUrlChange={handleTransactUrlChange}
                onShowPlans={handleShowPlans}
            />
            {props.initialState.filters && (
                <Filters
                    filtersData={props.initialState.filters}
                    suppliers={props.searchSettings.suppliers}
                    criteria={props.criteria}
                    allConnectionsSelected={props.searchSettings.allConnectionsSelected}
                    hideSupplierFilter={props.searchSettings.hideSupplierFilter}
                    hideConnectionFilter={props.searchSettings.hideConnectionFilter}
                    isSortByOnlyVisible={props.searchSettings.isSortByOnlyVisible}
                    showMaxUpfrontSlider={props.searchSettings.showMaxUpfrontSlider}
                    unavailableConnectionTypes={props.searchSettings.unavailableConnectionTypes}
                    connectionTypesToIgnore={props.searchSettings.connectionTypesToIgnore}
                    legacyActions={legacyActions.current}
                    showModal={handleShowModal}
                    sortMenu={props.initialState.filters?.sortMenu}
                    dataWidth="100%"
                />
            )}
        </>
    );
};

const SearchConnected = connector(SearchComponent);

export default function Search(props: SearchModel) {
    return (
        <AppComponent>
            <SearchConnected initialState={props} />
        </AppComponent>
    );
}
